import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePartnerSelection, ALL_PARTNERS } from '../core/PartnerContext';
import { personDisplayName } from '../core/personName';
import { partnerSearchRecord } from '../core/partnerSearch';
import { findMagicLeaf, magicNeedle } from '../../../utils/magicSearch';

export const partnerLabel = (partner) =>
  `${partner.partnerNumber} ${personDisplayName(partner.partnerRel?.holder)}`;

/*
 * One partner against the typed text: number and name first, and where
 * they say nothing, the Magic Search over holder and contact — so a
 * partner is also found by the mail address or the phone number the
 * one line of the list has no room for. What it was found by is then
 * written underneath, because the line itself does not show it.
 *
 * Returns the entry to list, or null for a partner that stays out.
 */
const matchPartner = (partner, needle) => {
  const label = partnerLabel(partner);
  if (!needle || label.toLowerCase().includes(needle)) {
    return { uuid: partner.uuid, label };
  }
  const hint = findMagicLeaf(partnerSearchRecord(partner), needle);
  return hint ? { uuid: partner.uuid, label, hint } : null;
};

/*
 * The choice itself: a substring filter over number and name plus the
 * list it narrows down — without saying where it is shown. The sidebar
 * head drops it into a popup below its button, the collapsed icon rail
 * into a dialog, and both mean the same thing by it.
 *
 * `onDone` is called once the choice is made or given up on; whoever
 * opened the list decides what that means for the focus.
 */
export function PartnerChoices({ id = 'partner-selector-listbox', onDone }) {
  const { t } = useTranslation();
  const { partners, selectedPartner, allPartnersSelected, selectPartner } =
    usePartnerSelection();

  const [filter, setFilter] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const needle = magicNeedle(filter);
  const matches = partners
    .map((partner) => matchPartner(partner, needle))
    .filter(Boolean);
  /* choices as flat list: "all partners" first, then the matches */
  const choices = [{ uuid: ALL_PARTNERS, label: t('partner.all') }, ...matches];
  const clampedActiveIndex = Math.min(activeIndex, choices.length - 1);

  const choose = (uuid) => {
    selectPartner(uuid);
    onDone();
  };

  const onKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(Math.min(clampedActiveIndex + 1, choices.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(Math.max(clampedActiveIndex - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (choices[clampedActiveIndex]) {
          choose(choices[clampedActiveIndex].uuid);
        }
        break;
      case 'Escape':
        event.preventDefault();
        onDone();
        break;
      default:
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        className="partner-selector-filter"
        placeholder={t('partner.filter')}
        aria-label={t('partner.filter')}
        aria-expanded="true"
        aria-controls={id}
        aria-activedescendant={
          choices[clampedActiveIndex]
            ? `partner-option-${choices[clampedActiveIndex].uuid}`
            : undefined
        }
        value={filter}
        onChange={(event) => {
          setFilter(event.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={onKeyDown}
      />
      <ul
        role="listbox"
        id={id}
        className="partner-selector-options"
        aria-label={t('partner.select')}
      >
        {choices.map((choice, index) => (
          <li
            key={choice.uuid}
            id={`partner-option-${choice.uuid}`}
            role="option"
            aria-selected={
              choice.uuid === ALL_PARTNERS
                ? allPartnersSelected
                : choice.uuid === selectedPartner?.uuid
            }
            className={[
              'partner-selector-option',
              /* the "all partners" choice stands apart from the
                 concrete partners below it (separator via CSS) */
              choice.uuid === ALL_PARTNERS ? 'all-partners' : null,
              index === clampedActiveIndex ? 'is-active' : null,
            ]
              .filter(Boolean)
              .join(' ')}
            /* preventDefault keeps the filter input focused, so the
               wrapper onBlur does not close the popup before click */
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => choose(choice.uuid)}
          >
            <span className="partner-selector-option-label">
              {choice.label}
            </span>
            {/* only a partner found by something invisible carries one */}
            {choice.hint && (
              <span
                className="partner-selector-hint"
                title={t('picker.matchedIn')}
              >
                {choice.hint}
              </span>
            )}
          </li>
        ))}
        {matches.length === 0 && (
          <li className="partner-selector-empty">{t('partner.noMatches')}</li>
        )}
      </ul>
    </>
  );
}

/*
 * Sidebar head: dropdown with all partners visible to the logged-in
 * subject plus an "all partners" choice; with a single partner just a
 * fixed label, no dropdown. Against int/prod many partners are
 * visible, so the open dropdown offers a substring filter matching
 * partner number and name.
 */
export function PartnerSelector() {
  const { t } = useTranslation();
  const { partners, selectedPartner, allPartnersSelected, isLoading } =
    usePartnerSelection();

  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);

  if (isLoading) {
    return <p className="partner-selector">{t('common.loading')}</p>;
  }

  if (!selectedPartner && !allPartnersSelected) {
    return null;
  }

  if (partners.length === 1) {
    const label = partnerLabel(selectedPartner);
    return (
      <p className="partner-selector" title={label}>
        <span className="partner-selector-label">{label}</span>
      </p>
    );
  }

  const close = () => {
    setIsOpen(false);
    toggleRef.current?.focus();
  };

  const onBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const selectedLabel = allPartnersSelected
    ? t('partner.all')
    : partnerLabel(selectedPartner);

  return (
    <div className="partner-selector-wrapper" onBlur={onBlur}>
      <button
        type="button"
        ref={toggleRef}
        className="partner-selector partner-selector-toggle"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        /* the sidebar is too narrow for most partner names, so the
           button cuts the label off — the tooltip names the current
           partner in full, which is what the cut-off text hides */
        title={t('partner.selectCurrent', { partner: selectedLabel })}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <span className="partner-selector-label">{selectedLabel}</span>
      </button>
      {isOpen && (
        <div className="partner-selector-popup">
          <PartnerChoices onDone={close} />
        </div>
      )}
    </div>
  );
}
